import type { FileResult } from '@pz/server';
import { saveFile } from './download';

/**
 * Interactive export (request 18 of round 4): a report of the application as one self-contained HTML file. The person
 * who receives it opens it in any browser, without an account and without a network: the figures are written on the
 * charts, and pointing at a storey, a bar or a cell shows its details (the texts of the marks, data-tip). The file holds
 * only what the report shows to the person who exported it (the same rights as the screen and the PDF).
 */

/** The style sheets of the application, without web fonts (the file uses the fonts of the reader's system). */
function collectCss(): string {
  const out: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList | null = null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // a style sheet of another origin: not readable, not needed
    }
    for (const rule of Array.from(rules)) {
      if (rule.cssText.startsWith('@font-face')) continue;
      out.push(rule.cssText);
    }
  }
  return out.join('\n');
}

const EXTRA_CSS = `
html, body { background: #f3f5f8; }
body { margin: 0; color: #17212b; font-family: "Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif; }
.pz-export-main { max-width: 1180px; margin: 24px auto; padding: 24px; background: #fff; border: 1px solid #d9dfe6; border-radius: 12px; }
.pz-export-main .pz-report { display: block !important; position: static !important; }
.pz-export-note { max-width: 1180px; margin: 0 auto 32px; padding: 0 24px; font-size: 12px; color: #56626f; }
[data-tip] { cursor: help; }
#pz-tip { position: fixed; z-index: 10; max-width: 360px; padding: 6px 9px; border-radius: 6px; background: #17212b; color: #fff; font-size: 12px; line-height: 1.35; white-space: pre-line; pointer-events: none; box-shadow: 0 6px 18px rgb(0 0 0 / 0.25); }
#pz-tip[hidden] { display: none; }
@media print { html, body { background: #fff; } .pz-export-main { border: 0; margin: 0; padding: 0; max-width: none; } .pz-export-note, #pz-tip { display: none; } }
`;

/** Tooltip of the file: the text of the mark under the pointer (or with the keyboard focus). */
const TIP_SCRIPT = `(function(){var tip=document.getElementById('pz-tip');function place(x,y){var r=tip.getBoundingClientRect();var l=x+14,t=y+14;if(l+r.width>innerWidth-8)l=x-r.width-14;if(t+r.height>innerHeight-8)t=y-r.height-14;tip.style.left=Math.max(4,l)+'px';tip.style.top=Math.max(4,t)+'px';}
function show(el,x,y){var s=el&&el.getAttribute('data-tip');if(!s){tip.hidden=true;return;}tip.textContent=s;tip.hidden=false;place(x,y);}
document.addEventListener('mousemove',function(e){show(e.target.closest&&e.target.closest('[data-tip]'),e.clientX,e.clientY);});
document.addEventListener('focusin',function(e){var el=e.target.closest&&e.target.closest('[data-tip]');if(!el)return;var r=el.getBoundingClientRect();show(el,r.left+r.width/2,r.bottom);});
document.addEventListener('mouseleave',function(){tip.hidden=true;});document.addEventListener('scroll',function(){tip.hidden=true;},true);})();`;

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

function toBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

/**
 * Builds the file from a report that is rendered in the page (the element of the print report) and hands it to the
 * download (in the demo: the file preview). `note` is a line below the report (date, source).
 */
export function exportInteractiveHtml(opts: { element: HTMLElement; title: string; fileName: string; lang: string; note: string }) {
  const body = opts.element.outerHTML;
  const html = `<!doctype html>
<html lang="${esc(opts.lang)}" data-theme="light">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="generator" content="Projektzeit">
<title>${esc(opts.title)}</title>
<style>${collectCss()}
${EXTRA_CSS}</style>
</head>
<body>
<main class="pz-export-main">${body}</main>
<p class="pz-export-note">${esc(opts.note)}</p>
<div id="pz-tip" role="tooltip" hidden></div>
<script>${TIP_SCRIPT}</script>
</body>
</html>`;
  const file: FileResult = { fileName: opts.fileName, mime: 'text/html', base64: toBase64(html) };
  saveFile(file);
}
